import React from "react";
import Link from "next/link";
import CompareIcon from "@mui/icons-material/Compare";
import useTranslation from "next-translate/useTranslation";

import { makeStyles } from "tss-react/mui";
import dayjs from "dayjs";

import VerifiedIcon from "@mui/icons-material/Verified";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpertReviewScoreBadge from "components/Badge/ExpertReviewScoreBadge";

import {
  reviewInfoLight,
  textMuted,
  hexToRgb,
} from "../../assets/jss/nextjs-material-kit-pro";
import { MONTH_YEAR_FORMAT } from "../../settings/global-website-config";

const useStyles = makeStyles()((theme) => ({
  lightWeight: {
    fontWeight: "400",
  },
  reviewInfoLight,
  reviewDateDark: {
    ...textMuted,
    fontSize: "0.85rem",
  },
  reviewDate: { marginTop: theme.spacing(-1) },
  comparedByButton: {
    display: "flex",
    alignItems: "center",
    "& svg": { marginRight: theme.spacing(0.5) },
  },

  reviewDetailsTitleWhite: {
    display: "flex",
    paddingBottom: "4px",
    "& h4": {
      marginTop: "auto",
      marginBottom: "auto",
      minHeight: "unset",
    },
    "& a": {
      color: theme.palette.common.white,
    },
  },
  reviewDetailsTitleDark: {
    display: "flex",
    paddingBottom: "4px",
    "& h4": {
      marginTop: "auto",
      marginBottom: "auto",
      minHeight: "unset",
    },

    "& svg": {
      fontSize: "1rem",
      marginLeft: "4px",
      verticalAlign: "sub",
    },
  },
  reviewedProductsLight: {
    color: theme.palette.common.white,
    "& a": {
      color: `rgba(${hexToRgb(theme.palette.common.white)}, 0.6)`,
    },
  },
  reviewerTitle: {
    color: `rgba(${hexToRgb(theme.palette.common.white)}, 0.8)`,
    marginTop: "0px !important",
  },
}));
export default function ReviewDetails(props) {
  const { review, url, productId, basic, hideChannel } = props;
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  if (!review) return <div />;

  const { reviewDetails } = review;

  const { reviewer, publisher, reviewedProducts, reviewHeadline } =
    reviewDetails;
  const comparedProducts =
    reviewedProducts?.length &&
    reviewedProducts.filter((product) => product.id !== productId);

  const isWrittenReview = review.reviewDetails.writtenReview.pageUrl;
  const title =
    reviewer?.athleteDetails.firstName && reviewer.athleteDetails.lastName ? (
      <>
        <span className={classes.lightWeight}>
          {`${reviewer.athleteDetails.firstName} ${reviewer.athleteDetails.lastName}`}
          <VerifiedIcon sx={{ mx: 1, fontSize: 14 }} />
        </span>
      </>
    ) : (
      publisher?.title || reviewHeadline // NB When there is no publisher, it's a manufacturer video, so we show the video title instead
    );

  const link = hideChannel ? null : url && !reviewer ? (
    <h5 className={classes.reviewerTitle}>
      <a href={url} target="_blank" rel="noopener noreferrer">
        {title}
        {review.reviewDetails.videoReview.url && <OpenInNewIcon />}
      </a>
    </h5>
  ) : (
    <>
      <Link prefetch={false} href={`/athlete/${reviewer.slug}`}>
        {title}
      </Link>
      {publisher?.title}
    </>
  );
  return (
    <>
      {!basic && (
        <>
          <div>
            {reviewDetails.reviewScore && reviewDetails.scoreIsExplicit && (
              <ExpertReviewScoreBadge
                reviews={{
                  reviewsScore: reviewDetails.reviewScore,
                }}
                showScore
                isSingleScore
                hideDetails
                hideHeader
              />
            )}
          </div>
          <div
            className={
              isWrittenReview
                ? classes.reviewDetailsTitleWhite
                : classes.reviewDetailsTitleDark
            }
          >
            {" "}
            {link}
          </div>
          <div>{!url && <h4>{title}</h4>}</div>
          {review.reviewDetails.dateIsExplicit && (
            <p
              className={cx({
                [classes.reviewDate]: true,
                [classes.reviewInfoLight]:
                  review.reviewDetails.writtenReview.pageUrl,
                [classes.reviewDateDark]:
                  !review.reviewDetails.writtenReview.pageUrl,
              })}
            >
              {dayjs(review.date).format(MONTH_YEAR_FORMAT)}
            </p>
          )}
          {!productId || comparedProducts?.length ? (
            <div
              className={cx({
                [classes.reviewedProductsLight]: isWrittenReview,
              })}
            >
              {productId
                ? `${t("common:comparedVs")}:`
                : `${t("common:reviewed")}:`}
              {comparedProducts.map((product) => (
                <div key={`${url}-${product.uri}`}>
                  <Link href={product.uri} legacyBehavior>
                    <a className={classes.comparedByButton}>
                      <CompareIcon fontSize="sm" />
                      {product.title}
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
}
